// JSON-LD Schema Markup for all pages
// Structured data for better search engine understanding

import { startingOfferSchema } from "./pricing";

// Stable @ids so Organization, WebSite and SoftwareApplication resolve to one
// entity graph. Keep in sync with scripts/generate-static-seo-pages.js.
export const ORG_ID = "https://ayesalon.com/#organization";
export const WEBSITE_ID = "https://ayesalon.com/#website";
export const SOFTWARE_ID = "https://ayesalon.com/#software";

export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": ORG_ID,
  name: "AyeSalon",
  legalName: "Kautix AI Solutions LLP",
  url: "https://ayesalon.com",
  logo: "https://ayesalon.com/logo.png",
  description: "All-in-one salon management software for Indian salons and spas",
  foundingDate: "2025",
  email: "support@ayesalon.com",
  telephone: "+91-7090238960",
  areaServed: { "@type": "Country", name: "India" },
  sameAs: [
    "https://www.instagram.com/ayesalonofficial/",
    "https://www.facebook.com/people/AyeSalon/61585412532460/",
    "https://www.linkedin.com/company/ayesalon/",
    "https://www.crunchbase.com/organization/ayesalon",
    "https://share.google/namWskQmh0ClKJEjo",
  ],
  contactPoint: [
    {
      "@type": "ContactPoint",
      contactType: "Customer Support",
      telephone: "+91-7090238960",
      email: "support@ayesalon.com",
      availableLanguage: ["English", "Hindi"],
    },
    {
      "@type": "ContactPoint",
      contactType: "Sales",
      telephone: "+91-7090238960",
      email: "sales@ayesalon.com",
    },
  ],
  address: {
    "@type": "PostalAddress",
    streetAddress: "Tower-3, Golden I, 308, Greater Noida West Road, Tech Zone IV, Amrapali Leisure Valley",
    addressLocality: "Greater Noida",
    addressRegion: "Uttar Pradesh",
    postalCode: "201318",
    addressCountry: "IN",
  },
};

export const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  name: "AyeSalon",
  image: "https://ayesalon.com/logo.png",
  description: "Salon management software serving 500+ salons across India",
  url: "https://ayesalon.com",
  telephone: "+91-7090238960",
  email: "support@ayesalon.com",
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "Customer Service",
    telephone: "+91-7090238960",
    email: "support@ayesalon.com",
  },
  address: {
    "@type": "PostalAddress",
    streetAddress: "Tower-3, Golden I, 308, Greater Noida West Road, Tech Zone IV, Amrapali Leisure Valley",
    addressLocality: "Greater Noida",
    addressRegion: "Uttar Pradesh",
    postalCode: "201318",
    addressCountry: "IN",
  },
};

export const homePageSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": WEBSITE_ID,
  name: "AyeSalon",
  url: "https://ayesalon.com/",
  publisher: { "@id": ORG_ID },
};

export const speakableSchema = (cssSelectors = []) => ({
  "@context": "https://schema.org",
  "@type": "WebPage",
  speakable: {
    "@type": "SpeakableSpecification",
    cssSelector: cssSelectors.length > 0 ? cssSelectors : ["h1", "main"],
  },
});

export const breadcrumbSchema = (path, items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: item.name,
    item: `https://ayesalon.com${item.url}`,
  })),
});

export const productSchema = (featureName, description, image) => ({
  "@context": "https://schema.org",
  "@type": "Product",
  name: `AyeSalon - ${featureName}`,
  description: description,
  image: image,
  brand: {
    "@type": "Brand",
    name: "AyeSalon",
  },
  offers: { ...startingOfferSchema, availability: "https://schema.org/InStock" },
});

export const softwareApplicationSchema = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "@id": SOFTWARE_ID,
  provider: { "@id": ORG_ID },
  name: "AyeSalon",
  description: "All-in-one salon management software for Indian salons. Manage appointments, staff, inventory, memberships & marketing.",
  url: "https://ayesalon.com",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web, iOS, Android",
  offers: startingOfferSchema,
};

export const featureDetailSchema = (featureName, description, shortDescription, pageUrl) => ({
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: `${featureName} — AyeSalon`,
  description: shortDescription,
  url: pageUrl ? `https://ayesalon.com${pageUrl.startsWith('/') ? pageUrl : '/' + pageUrl}` : "https://ayesalon.com",
  about: {
    "@type": "SoftwareApplication",
    name: "AyeSalon",
    applicationCategory: "BusinessApplication",
    description: description,
    offers: startingOfferSchema,
  },
});

export const faqSchema = (faqItems) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqItems.map((item) => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: item.answer,
    },
  })),
});

export const pricingPageSchema = (pricingTiers) => ({
  "@context": "https://schema.org",
  "@type": "OfferCatalog",
  name: "AyeSalon Pricing Plans",
  description: "Flexible pricing plans for salons of all sizes",
  hasOffer: pricingTiers.map((tier) => ({
    "@type": "Offer",
    name: tier.name,
    price: tier.price,
    priceCurrency: "INR",
    description: tier.description,
    availability: "https://schema.org/InStock",
    url: "https://ayesalon.com/pricing/",
  })),
});

export const contactPageSchema = {
  "@context": "https://schema.org",
  "@type": "ContactPage",
  name: "Contact AyeSalon",
  url: "https://ayesalon.com/contact",
  description: "Get in touch with our sales team for a personalized demo",
  potentialAction: {
    "@type": "CommunicateAction",
    target: {
      "@type": "EntryPoint",
      urlTemplate: "https://ayesalon.com/contact",
    },
  },
};

export const aboutPageSchema = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  name: "About AyeSalon",
  url: "https://ayesalon.com/about/",
  description: "About AyeSalon: salon management software for Indian salons, built and operated by Kautix AI Solutions LLP, Greater Noida.",
  mainEntity: { "@id": ORG_ID },
};

// Feature schemas for all feature pages
export const featureSchemas = {
  appointmentScheduling: {
    name: "Appointment Scheduling",
    description: "Smart appointment scheduling with automated reminders, real-time availability, and no-show reduction",
    image: "https://ayesalon.com/og-appointment.jpg",
  },
  staffManagement: {
    name: "Staff Management",
    description: "Efficient staff scheduling, performance tracking, commission management, and team productivity tools",
    image: "https://ayesalon.com/og-staff.jpg",
  },
  clientsManagement: {
    name: "Client Management",
    description: "Complete client database with service history, preferences, and personalized profiles",
    image: "https://ayesalon.com/og-clients.jpg",
  },
  inventoryManagement: {
    name: "Inventory Management",
    description: "Real-time inventory tracking, stock alerts, expense management, and purchasing optimization",
    image: "https://ayesalon.com/og-inventory.jpg",
  },
  discountOffers: {
    name: "Discount & Offers",
    description: "Create and manage promotions, automated discount application, and campaign tracking",
    image: "https://ayesalon.com/og-discounts.jpg",
  },
  clientAnalytics: {
    name: "Reports & Client Analytics",
    description: "Auto-generated sales, staff, and GST reports plus deep analytics on client behavior, revenue insights, popular services, and business metrics",
    image: "https://ayesalon.com/og-analytics.jpg",
  },
  invoiceSystem: {
    name: "Invoice System",
    description: "Automated invoicing, split payment across cash/card/UPI/wallet, payment tracking, billing reports, and professional invoice generation",
    image: "https://ayesalon.com/og-invoice.jpg",
  },
  membershipManagement: {
    name: "Membership Management",
    description: "Create membership programs, automated renewals, loyalty tracking, and member benefits",
    image: "https://ayesalon.com/og-membership.jpg",
  },
  gstAccounting: {
    name: "GST Accounting",
    description: "Automatic GST calculation, CGST/SGST split, GSTIN-compliant invoices, and GSTR-3B ready tax reports",
    image: "https://ayesalon.com/og-gst.jpg",
  },
  loyaltyRewards: {
    name: "Loyalty & Rewards",
    description: "Points-based loyalty program with tiered rewards, birthday bonuses, referral points, and automatic redemption tracking",
    image: "https://ayesalon.com/og-loyalty.jpg",
  },
  multiBranchManagement: {
    name: "Multi-Branch Management",
    description: "Centralized dashboard to manage unlimited salon branches with branch-level permissions, cross-branch inventory, and consolidated reporting",
    image: "https://ayesalon.com/og-multibranch.jpg",
  },
  staffAttendancePayroll: {
    name: "Staff Attendance & Payroll",
    description: "Mobile check-in/check-out, automatic overtime calculation, and commission-based payroll with instant payslips for salon staff",
    image: "https://ayesalon.com/og-staff.jpg",
  },
  expenseManagement: {
    name: "Expense Management",
    description: "Categorized, branch-wise expense and supplier bill tracking so salon owners see real profit, not just revenue",
    image: "https://ayesalon.com/og-inventory.jpg",
  },
};

// HowTo schema for feature guides
export const howToSchema = (featureName, steps) => ({
  "@context": "https://schema.org",
  "@type": "HowTo",
  name: `How to use ${featureName} in AyeSalon`,
  description: `Step-by-step guide to using ${featureName} feature in AyeSalon salon management software`,
  image: "https://ayesalon.com/logo.png",
  step: steps.map((step, index) => ({
    "@type": "HowToStep",
    position: index + 1,
    name: step.name,
    text: step.description,
    image: step.image || "https://ayesalon.com/logo.png",
  })),
});

// Review schema for testimonials
export const reviewSchema = (author, rating, reviewText) => ({
  "@context": "https://schema.org",
  "@type": "Review",
  author: {
    "@type": "Person",
    name: author,
  },
  reviewRating: {
    "@type": "Rating",
    ratingValue: rating,
    bestRating: "5",
    worstRating: "1",
  },
  reviewBody: reviewText,
  datePublished: "2026-04-27",
  itemReviewed: {
    "@type": "SoftwareApplication",
    name: "AyeSalon",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
  },
});

// Event schema for webinars/demos
export const eventSchema = (eventName, startTime, endTime) => ({
  "@context": "https://schema.org",
  "@type": "Event",
  name: eventName,
  startDate: startTime,
  endDate: endTime,
  eventStatus: "https://schema.org/EventScheduled",
  eventAttendanceMode: "https://schema.org/OnlineEventAttendanceMode",
  location: {
    "@type": "VirtualLocation",
    url: "https://ayesalon.com/app",
  },
  organizer: {
    "@type": "Organization",
    name: "AyeSalon",
    url: "https://ayesalon.com",
  },
  offers: {
    "@type": "Offer",
    name: "Free Demo",
    url: "https://ayesalon.com/app",
    price: "0",
    priceCurrency: "INR",
    availability: "https://schema.org/InStock",
    validFrom: "2026-04-27T00:00",
  },
});

// Service schema for salon services
export const serviceSchema = (serviceName, description, areaServed) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name: serviceName,
  description: description,
  provider: {
    "@type": "Organization",
    name: "AyeSalon",
    url: "https://ayesalon.com",
  },
  areaServed: areaServed || "IN",
  availableChannel: {
    "@type": "ServiceChannel",
    serviceUrl: "https://ayesalon.com/app",
    availableLanguage: "en-IN",
  },
});

// Video schema for demo videos
export const videoSchema = (videoTitle, description, thumbnailUrl, uploadDate) => ({
  "@context": "https://schema.org",
  "@type": "VideoObject",
  name: videoTitle,
  description: description,
  thumbnailUrl: [thumbnailUrl],
  uploadDate: uploadDate,
  duration: "PT5M",
  contentUrl: "https://ayesalon.com/app",
});

// Job posting schema (for hiring pages)
export const jobPostingSchema = (jobTitle, employmentType, baseSalary, location) => ({
  "@context": "https://schema.org",
  "@type": "JobPosting",
  title: jobTitle,
  description: `Join AyeSalon team as ${jobTitle}`,
  employmentType: employmentType,
  baseSalary: {
    "@type": "PriceSpecification",
    currency: "INR",
    price: baseSalary,
  },
  jobLocation: {
    "@type": "Place",
    address: {
      "@type": "PostalAddress",
      addressCountry: "IN",
      addressLocality: location,
    },
  },
  hiringOrganization: {
    "@type": "Organization",
    name: "AyeSalon",
    sameAs: "https://ayesalon.com",
  },
  jobStartDate: "2026-05-01",
  validThrough: "2026-06-30",
});
